import { Inject, Injectable } from '@angular/core';

import { AuthService } from '@fleet/auth/services/auth.service';
import {
  AssetGroupModel,
  AssetModel,
  ContractModel,
  CourseModel,
  DriverModel,
  JobModel,
  NetworkGroupModel,
  NetworkUserModel,
  OperatorModel,
  OperatorUserModel,
  OrganisationGroupModel,
  OrganisationModel,
  OrganisationStaffModel,
  OrganisationUserModel,
  PolicyModel,
  PromotionModel,
  TaxIdentifierModel,
  TravellerModel,
  VehicleModel,
} from '@fleet/model';
import { getLogoUrl } from '@fleet/utilities/logo-utilities';

import { JobStatusPipe, MobileFormatPipe } from '@fleet/pipes';
import { RulesetModel } from '@fleet/shared';
import {
  entityHeaderFromJob,
  entityHeaderFromTraveller,
  entityHeaderFromVehicle,
  getDriverRoutePrefix,
  getOrganisationRoutePrefix,
  internationalizedDate,
  titleCaseClean,
  vehicleEntityHeaderBreadcrumbs,
  VehicleEntityHeaderContext,
} from '@fleet/utilities';
import { BehaviorSubject } from 'rxjs';

import { LocaleService } from '@fleet/locale/locale-service/locale.service';
import { Breadcrumb, EntityDataItem, EntityHeader } from '@fleet/model';
import { NetworkGroupService } from '@fleet/network-group';
import { getOperatorRoutePrefix } from '@fleet/utilities';
import { TranslocoService } from '@jsverse/transloco';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class EntityHeaderService {
  entityHeader: BehaviorSubject<EntityHeader> = new BehaviorSubject(null);
  orgHeader: BehaviorSubject<EntityHeader> = new BehaviorSubject(null);
  operatorHeader: BehaviorSubject<EntityHeader> = new BehaviorSubject(null);
  networkGroupHeader: BehaviorSubject<EntityHeader> = new BehaviorSubject(null);
  fleetProduct: string;
  organisationId: string;
  operatorId: string;
  routeData: BehaviorSubject<any> = new BehaviorSubject(null);
  private selectedNetworkGroup: NetworkGroupModel = null;

  constructor(
    @Inject('env') env: any,
    private localeService: LocaleService,
    private translocoService: TranslocoService,
    private networkGroupService: NetworkGroupService,
    private authService: AuthService
  ) {
    this.fleetProduct = env.fleetProduct;

    // Subscribe to network group changes
    this.networkGroupService.networkGroup$.subscribe((group) => {
      this.selectedNetworkGroup = group;
    });
  }

  // luxonFormatPipe = new LuxonFormatPipe();
  jobStatusPipe = new JobStatusPipe();

  mobileFormPipe = new MobileFormatPipe();

  get entityHeader$() {
    return this.entityHeader.asObservable();
  }

  get routeData$() {
    return this.routeData.asObservable();
  }

  get orgHeader$() {
    return this.orgHeader.asObservable();
  }

  get operatorHeader$() {
    return this.operatorHeader.asObservable();
  }

  set organisation(entityHeader: EntityHeader) {
    this.orgHeader.next(entityHeader);
  }

  set operator(entityHeader: EntityHeader) {
    this.operatorHeader.next(entityHeader);
  }

  setEntityHeader(entityHeader: EntityHeader) {
    this.entityHeader.next(entityHeader);
  }

  setOrganisationId(organisationId: string) {
    this.organisationId = organisationId;
  }

  setOperatorId(operatorId: string) {
    this.operatorId = operatorId;
  }

  setEntityHeaderTitleOnly() {}

  setRouteData(routeData: any) {
    this.routeData.next(routeData);
  }

  setEntityHeaderCourseModel(
    course: CourseModel,
    pageLabel: string,
    type: string
  ) {
    const breadcrumbs: Breadcrumb[] = [
      {
        label: 'Course',
        clickable: true,
        prefixArrow: false,
        link: 'learning/search',
      },
      {
        label: course.name,
        prefixArrow: true,
        clickable: true,
        link: 'learning/' + course.courseId,
      },
    ];
    if (type === 'SETTINGS') {
      breadcrumbs.push({
        label: 'Settings',
        clickable: false,
        prefixArrow: true,
      });
    }

    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });

    const dataItems: any = [];

    if (course.effectiveTo) {
      dataItems.push({
        label: course.effectiveTo
          ? DateTime.fromISO(course.effectiveTo.toString()).toFormat('D')
          : '',
        icon: 'calendar_today',
        toolTip: 'Effective to',
      });
    }

    if (course.type) {
      dataItems.push({
        label: course.type,
        icon: 'category',
        toolTip: 'Type',
      });
    }
    dataItems.push({
      isChip: true,
      label: course.status,
      toolTip: 'Status',
    });

    this.entityHeader.next({
      title: course.name,
      dataItems: dataItems,
      breadcrumbs: breadcrumbs,
    });
  }
  setEntityHeaderGroupOrganisation(
    group: OrganisationGroupModel,
    pageLabel: string,
    type: string
  ) {
    const orgUrl = getOrganisationRoutePrefix(
      this.fleetProduct,
      group.organisationId
    );
    const breadcrumbs: Breadcrumb[] = [
      {
        label: this.translocoService.translate('group'),
        clickable: true,
        prefixArrow: false,
        link: orgUrl + '/group/search',
      },
      {
        label: group.displayName,
        prefixArrow: true,
        clickable: type === 'settings',
        link: orgUrl + '/group/' + group.organisationGroupId,
      },
    ];
    if (type === 'Settings') {
      breadcrumbs.push({
        label: this.translocoService.translate('settings'),
        clickable: false,
        prefixArrow: true,
      });
    }
    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });

    const dataItems: EntityDataItem[] = [
      {
        isChip: true,
        label: group.status,
      },
    ];

    if (group.legalName) {
      dataItems.push({
        label: group.legalName,
        icon: 'badge',
        toolTip: this.translocoService.translate('legalName'),
      });
    }

    dataItems.push(
      {
        label: group.code,
        icon: 'groups',
        toolTip: this.translocoService.translate('groupCode'),
      },
      {
        label: titleCaseClean(group.type),
        icon: 'business',
        toolTip: this.translocoService.translate('groupType'),
      }
    );

    this.entityHeader.next({
      title: group?.displayName,
      dataItems,
      avatar: {
        image: null,
        initials: group?.displayName[0],
      },
      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderTollRoadGroup(
    tollRoadGroup: any,
    tollSet: any,
    pageLabel: string
  ) {
    const dataItems = [
      {
        label: tollRoadGroup.code,
        icon: 'code',
        toolTip: 'Code',
      },
      {
        label: tollRoadGroup.name,
        icon: 'label',
        toolTip: 'Name',
      },
    ] as any;

    const breadcrumbs: Breadcrumb[] = [
      {
        label: 'Tollway',
        clickable: true,
        prefixArrow: false,
        link: '/settings/tollway/search',
      },
      {
        label: tollSet.name,
        prefixArrow: true,
        clickable: true,
        link: '/settings/tollway/' + tollSet.tollSetId,
      },
      {
        label: 'Road Groups',
        prefixArrow: true,
        clickable: true,
        link: '/settings/tollway/' + tollSet.tollSetId + '/toll-road-group/',
      },
      {
        label: tollRoadGroup.name,
        prefixArrow: true,
        clickable: false,
      },
    ];

    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });

    this.entityHeader.next({
      title: tollRoadGroup.name,
      dataItems: dataItems,
      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderTollRoad(
    tollRoad: any,
    tollSet: any,
    groupCodeName: string,
    pageLabel: string
  ) {
    const dataItems = [
      {
        label: tollRoad.code,
        icon: 'code',
        toolTip: 'Group Code',
      },

      {
        label: tollRoad.name,
        icon: 'label',
        toolTip: 'Name',
      },
      ...(tollRoad.tollRoadGroupCode
        ? [
            {
              label: tollRoad.tollRoadGroupCode,
              toolTip: 'Road Group Code',
              icon: 'group_work',
            },
            {
              label: groupCodeName,
              icon: 'badge',
              toolTip: 'Road Group Name',
            },
          ]
        : []),
    ] as any;

    const breadcrumbs: Breadcrumb[] = [
      {
        label: 'Tollway',
        clickable: true,
        prefixArrow: false,
        link: '/settings/tollway/search',
      },
      {
        label: tollSet.name,
        prefixArrow: true,
        clickable: true,
        link: '/settings/tollway/' + tollSet.tollSetId,
      },
      {
        label: 'Roads',
        prefixArrow: true,
        clickable: true,
        link: '/settings/tollway/' + tollSet.tollSetId + '/toll-road',
      },
      {
        label: tollRoad.name,
        prefixArrow: true,
        clickable: false,
      },
    ];

    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });

    this.entityHeader.next({
      title: tollRoad.name,
      dataItems: dataItems,
      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderTollSet(tollSet: any, pageLabel: string) {
    const dataItems = [
      {
        label: tollSet.name,
        icon: 'label', // Assuming 'label' is appropriate for a name
        toolTip: 'Name',
      },
      {
        label: tollSet.cityCode,
        icon: 'location_city', // Icon representing a city
        toolTip: 'City code',
      },
      {
        label: tollSet.regionCode,
        icon: 'map', // 'map' can represent a region
        toolTip: 'Region code',
      },
      {
        label: tollSet.countryCode,
        icon: 'public', // 'public' can represent a country or globe
        toolTip: 'Country code',
      },

      {
        label: tollSet.status,
        toolTip: 'Status',
        isChip: true,
      },
    ] as any;

    const breadcrumbs: Breadcrumb[] = [
      {
        label: 'Tollway',
        clickable: true,
        prefixArrow: false,
        link: '/settings/tollway/search',
      },
      {
        label: tollSet.name,
        prefixArrow: true,
        clickable: true,
        link: '/settings/tollway/' + tollSet.tollSetId,
      },
    ];

    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });
    this.entityHeader.next({
      title: tollSet.name,
      dataItems: dataItems,
      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderPolicyOrganisation(
    policy: PolicyModel,
    pageLabel: string,
    type: string
  ) {
    const orgUrl = getOrganisationRoutePrefix(
      this.fleetProduct,
      policy.organisationId
    );
    const breadcrumbs: Breadcrumb[] = [
      {
        label: this.translocoService.translate('policies'),
        clickable: true,
        prefixArrow: false,
        link: orgUrl + '/policy/search',
      },
      {
        label: policy.name,
        prefixArrow: true,
        clickable: type === 'settings',
        link: orgUrl + '/policy/' + policy.policyId,
      },
    ];
    if (type === 'Settings') {
      breadcrumbs.push({
        label: this.translocoService.translate('settings'),
        clickable: false,
        prefixArrow: true,
      });
    }
    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });
    this.entityHeader.next({
      title: policy.name,
      dataItems: [],

      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderRulesSearchOrganisation(
    policy: PolicyModel,
    ruleset: RulesetModel,
    pageLabel: string,
    type: string
  ) {
    const orgUrl = getOrganisationRoutePrefix(
      this.fleetProduct,
      policy.organisationId
    );

    const breadcrumbs: Breadcrumb[] = [
      {
        label: this.translocoService.translate('policies'),
        clickable: true,
        prefixArrow: false,
        link: orgUrl + '/policy/search',
      },
      {
        label: policy.name,
        prefixArrow: true,
        clickable: true,
        link: orgUrl + '/policy/' + policy.policyId,
      },
      {
        label: this.translocoService.translate('versions'),
        clickable: true,
        prefixArrow: true,
        link: orgUrl + '/policy/' + policy.policyId + '/version',
      },

      {
        label: ruleset?.description
          ? (ruleset.description.length > 50
              ? ruleset.description.substring(0, 50) + '...'
              : ruleset.description) +
            ' (' +
            titleCaseClean(ruleset.status) +
            ')'
          : '' + titleCaseClean(ruleset.status) + ' version',
        clickable: false,
        prefixArrow: true,
        link:
          orgUrl +
          '/policy/' +
          policy.policyId +
          '/version/' +
          ruleset.reference,
      },
    ];

    const dataItems = [
      {
        label: ruleset.status,
        icon: ruleset.status === 'PUBLISHED' ? 'done' : 'pending',
        toolTip: this.translocoService.translate('status'),
        isChip: true,
      },
    ] as any;

    const effectiveFromLabel = internationalizedDate(
      ruleset.effectiveFrom,
      'DATETIME_SHORT',
      false,
      null,
      this.localeService.getLocale()
    );
    const effectiveToLabel = internationalizedDate(
      ruleset.effectiveTo,
      'DATETIME_SHORT',
      false,
      null,
      this.localeService.getLocale()
    );

    if (ruleset.effectiveFrom && ruleset.effectiveTo) {
      dataItems.push({
        label: `${effectiveFromLabel ? effectiveFromLabel : ''} ${
          effectiveToLabel ? ' - ' + effectiveToLabel : ''
        }`,
        icon: 'event',
        toolTip: this.translocoService.translate('effectiveFromTo'),
      });
    }

    this.entityHeader.next({
      title: ruleset?.description
        ? ruleset.description.length > 50
          ? ruleset.description.substring(0, 50) + '...'
          : ruleset.description
        : '' + titleCaseClean(ruleset.status) + ' version',
      dataItems: dataItems,

      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderRulesetSearchOrganisation(
    policy: PolicyModel,
    pageLabel: string,
    type: string
  ) {
    const orgUrl = getOrganisationRoutePrefix(
      this.fleetProduct,
      policy.organisationId
    );
    const breadcrumbs: Breadcrumb[] = [
      {
        label: this.translocoService.translate('policies'),
        clickable: true,
        prefixArrow: false,
        link: orgUrl + '/policy/search',
      },
      {
        label: policy.name,
        prefixArrow: true,
        clickable: type === 'settings',
        link: orgUrl + '/policy/' + policy.policyId,
      },
      {
        label: this.translocoService.translate('versions'),
        clickable: true,
        prefixArrow: true,
        link: orgUrl + '/policy/' + policy.policyId + '/version',
      },
    ];
    this.entityHeader.next({
      title: policy.name,
      dataItems: [],

      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderPromotionModel(
    promotion: PromotionModel,
    pageLabel: string,
    type: string
  ) {
    const breadcrumbs: Breadcrumb[] = [
      {
        label: this.translocoService.translate('promotions'),
        clickable: true,
        prefixArrow: false,
        link: 'settings/promotion/search',
      },
      {
        label: promotion.name,
        prefixArrow: true,
        clickable: true,
        link: 'settings/promotion/' + promotion.promotionId,
      },
    ];

    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });

    const dataItems: any = [];

    if (promotion.effectiveFrom) {
      const effectiveFromLabel = internationalizedDate(
        promotion.effectiveFrom,
        'CONDENSED_DATE',
        false,
        null,
        this.localeService.getLocale()
      );

      const effectiveToabel = internationalizedDate(
        promotion.effectiveFrom,
        'CONDENSED_DATE',
        false,
        null,
        this.localeService.getLocale()
      );

      dataItems.push({
        label:
          effectiveFromLabel +
          (promotion.effectiveTo ? ' - ' + effectiveToabel : ''),
        icon: 'calendar_today',
        toolTip: this.translocoService.translate('effectiveFromTo'),
      });
    }

    if (promotion.type) {
      dataItems.push({
        label: promotion.type,
        icon: 'category',
        toolTip: this.translocoService.translate('type'),
      });
    }

    this.entityHeader.next({
      title: promotion.name,
      dataItems: dataItems,
      avatar: {
        image: null,
        initials: null,
      },
      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderTravellerPersonalProfile(
    traveller: TravellerModel,
    pageLabel: string,
    type: string
  ) {
    if (!traveller) {
      this.entityHeader.next(null);
    } else {
      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('travellers'),
          clickable: true,
          prefixArrow: false,
          link: '/traveller/search',
        },
        {
          label:
            traveller.firstName +
            ' ' +
            (traveller.lastName ? traveller.lastName : ''),
          prefixArrow: true,
          clickable: type === 'settings',
          link: '/traveller/' + traveller.travellerId,
        },
      ];
      if (type === 'Settings') {
        breadcrumbs.push({
          label: this.translocoService.translate('settings'),
          clickable: false,
          prefixArrow: true,
        });
      }
      //Add the breadcrumb to go back to profile

      breadcrumbs.push({
        label: this.translocoService.translate('profiles'),
        prefixArrow: true,
        clickable: true,
        link: '/traveller/' + traveller.travellerId + '/settings/profile',
      });

      breadcrumbs.push({
        label: pageLabel,
        prefixArrow: true,
        clickable: false,
      });
      this.entityHeader.next({
        ...entityHeaderFromTraveller(traveller, this.fleetProduct),
        breadcrumbs: breadcrumbs,
      });
    }
  }

  setEntityHeaderAssetModel(
    asset: AssetModel,
    pageLabel: string,
    type: string
  ) {
    const breadcrumbs: Breadcrumb[] = [
      {
        label: this.translocoService.translate('assets'),
        clickable: true,
        prefixArrow: false,
        link: '/hardware/asset',
      },
      {
        label: asset.displayName,
        prefixArrow: true,
        clickable: type === 'SETTINGS',
        link: '/hardware/asset/' + asset.assetId,
      },
    ];
    if (type === 'SETTINGS') {
      breadcrumbs.push({
        label: this.translocoService.translate('settings'),
        clickable: false,
        prefixArrow: true,
      });
    }
    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });

    const dataItems: any = [
      {
        isChip: true,
        label: asset.assetStatus,
      },
      {
        label: asset.serialNo,
        icon: 'smartphone',
        toolTip: this.translocoService.translate('serialNo'),
      },
      {
        label: asset.assetGroupDisplayName,
        icon:
          asset?.allocatedTo === 'VEHICLE' || asset?.allocatedTo === 'DRIVER'
            ? 'local_taxi'
            : 'inventory_2',
        toolTip: this.translocoService.translate('assetGroup'),
        link:
          asset?.allocatedTo === 'VEHICLE'
            ? '/vehicle/' + asset.allocatedToId + '/settings/asset'
            : asset?.allocatedTo === 'DRIVER'
            ? '/driver/' + asset.allocatedToId + '/settings/status'
            : null,
      },
    ];

    if (asset.terminalId) {
      dataItems.push({
        label: asset.terminalId,
        icon: 'numbers',
        toolTip: this.translocoService.translate('tid'),
      });
    }

    this.entityHeader.next({
      title: asset.manufacturer + ' ' + asset.model,
      dataItems: dataItems,
      avatar: null,
      breadcrumbs: breadcrumbs,
    });
  }

  setEntityHeaderDriverModel(
    driver: DriverModel,
    pageLabel: string,
    type: string
  ) {
    if (!driver) {
      this.entityHeader.next(null);
    } else {
      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('drivers'),
          clickable: true,
          prefixArrow: false,
          link: '/driver/search',
        },
        {
          label:
            driver.firstName && driver.lastName
              ? driver.firstName + ' ' + driver.lastName
              : driver.preferredName
              ? driver.preferredName
              : 'Name Unknown',
          prefixArrow: true,
          clickable: type === 'SETTINGS',
          link: '/driver/' + driver.driverId,
        },
      ];
      if (type === 'SETTINGS') {
        breadcrumbs.push({
          label: this.translocoService.translate('settings'),
          clickable: false,
          prefixArrow: true,
        });
      }

      const dateLabel = internationalizedDate(
        driver.taxiAuthorityExpiry,
        'CONDENSED_DATE',
        false,
        null,
        this.localeService.getLocale()
      );

      let panelOne = {
        dataItems: [
          ...(driver.network
            ? [
                {
                  label: driver.network.networkGroupName,
                  icon: 'business',
                  toolTip: this.translocoService.translate('networkGroup'),
                },
              ]
            : []),
          {
            label: driver.taxiAuthorityNumber,
            secondaryLabel: '(exp:' + dateLabel + ')',
            icon: 'assignment_ind',
            toolTip: this.translocoService.translate('authorityNumber'),
          },

          {
            label: driver.username,
            icon: 'mail',
            toolTip: driver.username,
            link: 'mailto:' + driver.username,
            externalLink: true,
          },
          {
            dialablePhoneNumber:
              this.fleetProduct === 'HUB'
                ? {
                    phoneNumber: driver.phoneNumber,
                    tooltip: this.translocoService.translate('callDriver'),
                    phoneNumberContext: {
                      driverId: driver.driverId,
                      organisationId: this.organisationId,
                      name:
                        driver.preferredName ||
                        (driver.firstName && driver.lastName
                          ? `${driver.firstName} ${driver.lastName}`
                          : 'Name Unknown'),
                    },
                  }
                : null,
            label: this.mobileFormPipe.transform(driver.phoneNumber),
            icon: 'phone',
            toolTip: this.translocoService.translate('phoneNumber'),
            link: 'tel:' + driver.phoneNumber,
            externalLink: true,
          },
        ],
      };

      //shift status

      const panelTwo = {
        dataItems: [] as any,
      };

      if (driver.shift) {
        if (driver.shift.penaltyType === 'DISPATCH_SUSPENSION') {
          panelTwo.dataItems.push({
            label:
              this.translocoService.translate('suspendedUntil') +
              ' ' +
              DateTime.fromISO(driver.shift.penaltyEnd.toString()).toFormat(
                'h:mm a'
              ) +
              ' ' +
              DateTime.fromISO(
                driver.shift.penaltyEnd.toString()
              ).toRelativeCalendar(),
            icon: 'warning',
          } as any);
        } else {
          panelTwo.dataItems.push({
            toolTip: this.translocoService.translate('shiftAvailability'),
            label: titleCaseClean(driver.shift.availability),
            icon: 'circle',
            iconStyle:
              driver.shift.availability === 'AVAILABLE'
                ? 'text-green-400 icon-size-3 items-center w-full mr-2'
                : driver.shift.availability === 'OFFLINE'
                ? 'text-red-400 icon-size-3 items-center w-full ml-1.5 mr-2.5'
                : driver.shift.availability === 'BUSY'
                ? 'text-orange-400 icon-size-3 items-center w-full mr-2'
                : 'text-gray-400 icon-size-3 items-center w-full mr-2',
          } as any);

          panelTwo.dataItems.push({
            icon: 'local_taxi',
            toolTip: this.translocoService.translate('shiftStatus'),
            label: titleCaseClean(driver.shift.shiftStatus, {
              capitalCase: true,
            }),
          } as any);

          panelTwo.dataItems.push({
            toolTip: this.translocoService.translate('appVersion'),
            icon:
              !driver?.device || driver?.device?.deviceOs === 'UNKNOWN'
                ? 'device_unknown'
                : null,
            svgIcon:
              driver?.device?.deviceOs === 'ANDROID'
                ? 'vendors:android-green'
                : driver?.device?.deviceOs === 'IOS'
                ? 'vendors:apple-black'
                : null,
            label:
              driver.shift.appVersion +
              ' (' +
              titleCaseClean(driver.shift.appStatus) +
              ')',
          } as any);
        }
      } else {
        panelTwo.dataItems.push({
          label: this.translocoService.translate('notOnShift'),
          icon: 'local_taxi',
        } as any);
      }

      const panels = [panelOne, panelTwo];

      breadcrumbs.push({
        label:
          pageLabel === 'Remittances'
            ? this.translocoService.translate('driverRemittances')
            : pageLabel,
        prefixArrow: true,
        clickable: false,
      });

      this.entityHeader.next({
        title: driver.preferredName ? driver.preferredName : 'Name Unknown',
        subtitle:
          driver.firstName && driver.lastName
            ? driver.firstName +
              (driver.middleName ? ' ' + driver.middleName : '') +
              ' ' +
              driver.lastName
            : 'Name Unknown',
        avatar: {
          image: driver.driverPhoto ? driver.driverPhoto.url : null,
        },
        subtitleTooltip:
          driver.firstName +
          (driver.middleName ? ' ' + driver.middleName : '') +
          ' ' +
          driver.lastName,
        titleTooltip: driver.preferredName,
        status: driver.status,
        statusLink: '/driver/' + driver.driverId + '/settings/status',
        panels: panels,
        breadcrumbs: breadcrumbs,
      });
    }
  }

  setEntityHeaderJobModel(
    job: JobModel,

    pageLabel: string,
    type: string
  ) {
    if (!job) {
      this.entityHeader.next(null);
    } else {
      const orgUrl = getOrganisationRoutePrefix(
        this.fleetProduct,
        this.organisationId
      );

      const header = entityHeaderFromJob(
        job,
        this.fleetProduct,
        true,
        pageLabel,
        orgUrl,
        false
      );
      this.entityHeader.next({ ...header });
    }
  }

  setEntityHeaderTravellerModel(
    traveller: TravellerModel,

    pageLabel: string,
    type: string
  ) {
    if (!traveller) {
      this.entityHeader.next(null);
    } else {
      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('travellers'),
          clickable: true,
          prefixArrow: false,
          link: '/traveller/search',
        },
        {
          label:
            traveller.firstName +
            ' ' +
            (traveller.lastName ? traveller.lastName : ''),
          prefixArrow: true,
          clickable: type === 'settings',
          link: '/traveller/' + traveller.travellerId,
        },
      ];
      if (type === 'Settings') {
        breadcrumbs.push({
          label: this.translocoService.translate('settings'),
          clickable: false,
          prefixArrow: true,
        });
      }
      breadcrumbs.push({
        label: pageLabel,
        prefixArrow: true,
        clickable: false,
      });
      this.entityHeader.next({
        ...entityHeaderFromTraveller(traveller, this.fleetProduct),
        breadcrumbs: breadcrumbs,
      });
    }
  }

  setEntityHeaderShipmentModel(shipment: AssetGroupModel) {
    if (!shipment) {
      this.entityHeader.next(null);
    } else {
      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('shipments'),
          clickable: true,
          prefixArrow: false,
          link: '/hardware/shipment',
        },
        {
          label: shipment.displayName
            ? shipment.displayName
            : shipment.description,
          prefixArrow: true,
          clickable: false,
          link: '/hardware/shipment/' + shipment.assetGroupId,
        },
      ];
      const dataItems = [];
      if (shipment.consignmentStatus) {
        dataItems.push({
          isChip: true,
          label: shipment.consignmentStatus,
          toolTip: this.translocoService.translate('status'),
        });
      }
      if (shipment.collectionMethod) {
        dataItems.push({
          icon: 'local_shipping',
          label: titleCaseClean(shipment.collectionMethod),
          toolTip: this.translocoService.translate('collectionMethod'),
        });
      }
      if (shipment.consignmentType) {
        dataItems.push({
          icon: 'location_on',
          label: titleCaseClean(shipment.consignmentType),
          toolTip: this.translocoService.translate('consignmentType'),
        });
      }
      if (shipment.consignmentNumber) {
        dataItems.push({
          icon: 'tag',
          label: titleCaseClean(shipment.consignmentNumber),
          toolTip: this.translocoService.translate('consignmentNumber'),
        });
      }
      if (shipment.contactName) {
        dataItems.push({
          icon: 'person',
          label: shipment.contactName,
          toolTip: this.translocoService.translate('contactName'),
        });
      }
      if (shipment.contactNumber) {
        dataItems.push({
          icon: 'phone',
          label: titleCaseClean(shipment.contactNumber),
          toolTip: this.translocoService.translate('contactNumber'),
        });
      }
      if (shipment.referenceNumber) {
        dataItems.push({
          icon: 'tag',
          label: shipment.referenceNumber,
          toolTip: this.translocoService.translate('assetReference'),
        });
      }

      this.entityHeader.next({
        title: shipment.displayName
          ? shipment.displayName
          : shipment.description,
        dataItems: dataItems,

        breadcrumbs: breadcrumbs,
      });
    }
  }

  setEntityHeaderVehicleModel(
    vehicle: VehicleModel,
    pageLabel: string,
    type: string,
    operator?: OperatorModel
  ) {
    if (!vehicle) {
      this.entityHeader.next(null);
    } else {
      const pageType = type === 'SETTINGS' ? 'SETTINGS' : 'DETAIL';
      const vehicleTitle = vehicle.plate
        ? vehicle?.plate?.plateNumber
        : vehicle?.displayName;
      const statusLink = operator
        ? '/operator/' +
          operator.operatorId +
          '/vehicle/' +
          vehicle.vehicleId +
          '/settings/status'
        : '/vehicle/' + vehicle.vehicleId + '/settings/status';

      const breadcrumbs = vehicleEntityHeaderBreadcrumbs({
        vehiclesLabel: this.translocoService.translate('vehicles'),
        settingsLabel: this.translocoService.translate('settings'),
        pageLabel,
        vehicleTitle,
        vehicleId: vehicle.vehicleId,
        operatorId: operator?.operatorId,
        pageType,
      });

      const vehicleHeaderContext: VehicleEntityHeaderContext = {
        statusLink,
        showMedallion: this.authService.userHasFunction(
          'MEDALLION_DETAIL',
          'read'
        ),
        tooltips: {
          status: this.translocoService.translate('status'),
          makeModel: this.translocoService.translate('makeModel'),
          bodyType: this.translocoService.translate('bodyType'),
          manufactureYear: this.translocoService.translate('manufactureYear'),
        },
      };

      this.entityHeader.next({
        ...entityHeaderFromVehicle(vehicle, vehicleHeaderContext),
        breadcrumbs,
      });
    }
  }

  setOperatorHeaderFromOperatorMode(
    operator: OperatorModel,
    pageLabel: string,
    type: string
  ) {
    if (!operator) {
      this.entityHeader.next(null);
    } else {
      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('operators'),
          clickable: true,
          prefixArrow: false,
          link: '/operator/search',
        },
        {
          label: operator.displayName,
          prefixArrow: true,
          clickable: type === 'SETTINGS',
          link: '/operator/' + operator.operatorId,
        },
      ];
      if (type === 'SETTINGS') {
        breadcrumbs.push({
          label: this.translocoService.translate('settings'),
          clickable: false,
          prefixArrow: true,
        });
      }
      breadcrumbs.push({
        label: pageLabel,
        prefixArrow: true,
        clickable: false,
      });

      const dataItems: any[] = [];

      dataItems.push({
        isChip: true,
        link: '/operator/' + operator.operatorId + '/settings/status',

        label: operator.status,
        toolTip: this.translocoService.translate('status'),
      });

      if (operator.legalName) {
        dataItems.push({
          label: operator.legalName,
          icon: 'badge',
          toolTip: this.translocoService.translate('legalName'),
        });
      }

      if (operator.contactNumber) {
        dataItems.push({
          label: operator.contactNumber,
          icon: 'phone',
          toolTip: this.translocoService.translate('phoneNumber'),
        });
      }

      if (operator.contactEmail) {
        dataItems.push({
          label: operator.contactEmail,
          icon: 'mail',
          toolTip: this.translocoService.translate('email'),
        });
      }

      if (operator.taxIdentifiers) {
        operator.taxIdentifiers
          .filter((taxIdentifier: TaxIdentifierModel) => !taxIdentifier.deleted)
          .forEach((taxIdentifier: TaxIdentifierModel) => {
            dataItems.push({
              label: taxIdentifier.identifier,
              icon: 'business',
              toolTip: taxIdentifier.type,
            });
          });
      }

      this.operatorHeader.next({
        title: operator.displayName,
        dataItems: dataItems,
        avatar: null,
        breadcrumbs: breadcrumbs,
      });
    }
  }

  setOrgHeaderFromOrganisationModel(
    org: OrganisationModel,
    pageLabel: string,
    type: string
  ) {
    if (!org) {
      this.orgHeader.next(null);
    } else {
      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('organisations'),
          clickable: true,
          prefixArrow: false,
          link: '/organisation/search',
        },
        {
          label: org.name,
          prefixArrow: true,
          clickable: type === 'SETTINGS',
          link: '/organisation/' + org.organisationId,
        },
      ];
      if (type === 'SETTINGS') {
        breadcrumbs.push({
          label: this.translocoService.translate('settings'),
          clickable: false,
          prefixArrow: true,
        });
      }
      breadcrumbs.push({
        label: pageLabel,
        prefixArrow: true,
        clickable: false,
      });

      const dataItems: any[] = [
        {
          isChip: true,
          label: org.status,
        },
      ];

      if (org.legalName) {
        dataItems.push({
          label: org.legalName,
          icon: 'badge',
          toolTip: this.translocoService.translate('legalName'),
        });
      }

      dataItems.push(
        {
          label: org.organisationCode,
          icon: 'tag',
          toolTip: this.translocoService.translate('organisationCode'),
        },
        {
          label: org.emailDomain,
          icon: 'mail',
          toolTip: this.translocoService.translate('email'),
        }
      );

      if (org.taxIdentifiers) {
        org.taxIdentifiers
          .filter((taxIdentifier: TaxIdentifierModel) => !taxIdentifier.deleted)
          .forEach((taxIdentifier: TaxIdentifierModel) => {
            dataItems.push({
              label: taxIdentifier.identifier,
              icon: 'business',
              toolTip: taxIdentifier.type,
            });
          });
      }

      this.orgHeader.next({
        title: org.name,
        dataItems: dataItems,

        avatar: org.emailDomain
          ? {
              image: getLogoUrl(org.emailDomain),
              initials: org.name.substring(0, 1),
            }
          : null,
        breadcrumbs: breadcrumbs,
      });
    }
  }

  /**
   * `staffLabelPlural` is the organisation's own word for its people (from `OrganisationLabelsService`,
   * e.g. "Students"/"Payers"/"Staff") — the caller already resolves it (it has the staff record's type),
   * so this stays the one place the breadcrumb is assembled rather than duplicating the lookup per page.
   */
  setEntityHeaderStaffOrganisation(
    staff: OrganisationStaffModel,

    pageLabel: string,
    type: string,
    staffLabelPlural?: string
  ) {
    if (!staff) {
      this.entityHeader.next(null);
    } else {
      let staffSearchLink;

      const orgUrl = getOrganisationRoutePrefix(
        this.fleetProduct,
        staff.organisationId
      );
      const breadcrumbs: Breadcrumb[] = [
        {
          label: staffLabelPlural ?? this.translocoService.translate('staff'),
          clickable: true,
          prefixArrow: false,
          link: orgUrl + '/staff/search',
        },
        {
          label: staff.firstName + ' ' + staff.lastName,
          prefixArrow: true,
          clickable: type === 'settings',
          link: orgUrl + '/staff/' + staff.organisationStaffId,
        },
      ];
      if (type === 'Settings') {
        breadcrumbs.push({
          label: this.translocoService.translate('settings'),
          clickable: false,
          prefixArrow: true,
        });
      }
      breadcrumbs.push({
        label: pageLabel,
        prefixArrow: true,
        clickable: false,
      });

      this.entityHeader.next({
        title: staff.firstName + ' ' + staff.lastName,
        dataItems: [
          {
            isChip: true,
            label: staff.status,
          },
          {
            label: staff.phoneNumber,
            icon: 'phone',
            toolTip: this.translocoService.translate('phoneNumber'),
          },
          {
            label: staff.email,
            icon: 'mail',
            toolTip: this.translocoService.translate('email'),
          },
        ],
        avatar: {
          image: null,
          initials: staff.firstName[0] + staff.lastName[0],
        },

        breadcrumbs: breadcrumbs,
      });
    }
  }

  setEntityHeaderNetworkUser(
    user: NetworkUserModel,

    pageLabel: string,
    type: string
  ) {
    if (!user) {
      this.entityHeader.next(null);
    } else {
      let userSearchLink;

      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('users'),
          clickable: true,
          prefixArrow: false,
          link: '/settings/security/user',
        },
        {
          label: user.firstName + ' ' + user.lastName,
          prefixArrow: true,
          clickable: type === 'settings',
          link: 'settings/security/user/' + user.networkUserId,
        },
      ];

      //No need to set the 'Settings' breadcrumb anymore.

      this.entityHeader.next({
        title: user.firstName + ' ' + user.lastName,
        dataItems: [
          {
            isChip: true,
            label: user.status,
          },
          {
            label: user.phoneNumber,
            icon: 'phone',
            toolTip: this.translocoService.translate('phoneNumber'),
          },
          {
            label: user.username,
            icon: 'mail',
            toolTip: this.translocoService.translate('email'),
          },
        ],
        avatar: null,
        breadcrumbs: breadcrumbs,
      });
    }
  }

  setEntityHeaderContractModel(
    contract: ContractModel,
    type: string,
    entity: any
  ) {
    if (!contract) {
      this.entityHeader.next(null);
    } else {
      const breadcrumbItems = [];

      let orgUrl: string;
      let operatorUrl: string;
      let driverUrl: string;
      if (type === 'organisation') {
        orgUrl = getOrganisationRoutePrefix(
          this.fleetProduct,
          this.organisationId
        );

        breadcrumbItems.push({
          label: this.translocoService.translate('billingContractSearch'),
          clickable: true,
          prefixArrow: false,
          link: orgUrl + '/settings/billing',
        });

        breadcrumbItems.push({
          label: contract.displayName,
          clickable: false,
          prefixArrow: true,
        });
      } else if (type === 'operator') {
        operatorUrl = getOperatorRoutePrefix(
          this.fleetProduct,
          this.operatorId
        );

        breadcrumbItems.push({
          label: this.translocoService.translate('billingContractSearch'),
          clickable: true,
          prefixArrow: false,
          link: operatorUrl + '/settings/billing',
        });

        breadcrumbItems.push({
          label: contract.displayName,
          clickable: false,
          prefixArrow: true,
        });
      } else if (type === 'driver') {
        driverUrl = getDriverRoutePrefix(this.fleetProduct, contract.driverId);
        breadcrumbItems.push({
          label: this.translocoService.translate('drivers'),
          clickable: true,
          prefixArrow: false,
          link: '/driver/search',
        });

        breadcrumbItems.push({
          label: entity.firstName + ' ' + entity.lastName,
          prefixArrow: true,
          clickable: true,
          link: driverUrl,
        });

        breadcrumbItems.push({
          label: this.translocoService.translate('settings'),
          clickable: true,
          prefixArrow: true,
          link: driverUrl + '/settings',
        });

        breadcrumbItems.push({
          label: contract.displayName,
          clickable: true,
          prefixArrow: true,
          link: driverUrl + '/settings/billing',
        });
      }

      const dataItems = [
        {
          label: contract.status,
          isChip: true,
        },
      ] as any[];

      if (contract.startDate) {
        dataItems.push({
          label: contract.startDate
            ? DateTime.fromISO(contract.startDate.toString()).toFormat('D')
            : '',
          icon: 'calendar_today',
          toolTip: 'Start date',
        });
      }

      if (contract.endDate) {
        dataItems.push({
          label: contract.endDate
            ? DateTime.fromISO(contract.endDate.toString()).toFormat('D')
            : '',
          icon: 'calendar_today',
          toolTip: 'End date',
        });
      }

      this.entityHeader.next({
        title: contract.displayName,
        dataItems: dataItems,
        breadcrumbs: breadcrumbItems,
      });
    }
  }

  setEntityHeaderUserOrganisation(
    user: OrganisationUserModel,

    pageLabel: string,
    type: string
  ) {
    if (!user) {
      this.entityHeader.next(null);
    } else {
      let userSearchLink;
      const orgUrl = getOrganisationRoutePrefix(
        this.fleetProduct,
        user.organisationId
      );
      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('users'),
          clickable: true,
          prefixArrow: false,
          link: orgUrl + '/settings/user',
        },
        {
          label: user.firstName + ' ' + user.lastName,
          prefixArrow: true,
          clickable: type === 'settings',
          link: orgUrl + '/settings/user/' + user.organisationUserId,
        },
      ];
      //Do not need to set the Settings breadcrumb on Org user anymore.

      this.entityHeader.next({
        title: user.firstName + ' ' + user.lastName,
        dataItems: [
          {
            isChip: true,
            label: user.status,
          },
          {
            label: user.phoneNumber,
            icon: 'phone',
            toolTip: this.translocoService.translate('phoneNumber'),
          },
          {
            label: user.username,
            icon: 'mail',
            toolTip: this.translocoService.translate('email'),
          },
        ],
        avatar: {
          image: null,
          initials: user.firstName[0] + user.lastName[0],
        },
        breadcrumbs: breadcrumbs,
      });
    }
  }

  setEntityHeaderOperatorUser(
    user: OperatorUserModel,
    pageLabel: string,
    type: string
  ) {
    if (!user) {
      this.entityHeader.next(null);
    } else {
      const operatorUrl = this.getOperatorRoutePrefix();
      const breadcrumbs: Breadcrumb[] = [
        {
          label: this.translocoService.translate('users'),
          clickable: true,
          prefixArrow: false,
          link: operatorUrl + '/settings/user',
        },
        {
          label: user.firstName + ' ' + user.lastName,
          prefixArrow: true,
          clickable: type === 'settings',
          link: operatorUrl + '/settings/user/' + user.operatorUserId,
        },
      ];
      //Do not need to set the 'settings' breadcrumb on an operator user.

      this.entityHeader.next({
        title: user.firstName + ' ' + user.lastName,
        dataItems: [
          {
            isChip: true,
            label: user.status,
          },
          {
            label: user.phoneNumber,
            icon: 'phone',
            toolTip: this.translocoService.translate('phoneNumber'),
          },
          {
            label: user.username,
            icon: 'mail',
            toolTip: this.translocoService.translate('email'),
          },
        ],
        avatar: {
          image: null,
          initials: user.firstName[0] + user.lastName[0],
        },
        breadcrumbs: breadcrumbs,
      });
    }
  }

  getOperatorRoutePrefix() {
    return this.fleetProduct === 'OPERATOR'
      ? ''
      : this.fleetProduct === 'HUB' && this.operatorId
      ? '/operator/' + this.operatorId
      : '';
  }
}
